import { EyeIcon } from "@heroicons/react/16/solid";
import { useContext, useEffect, useState } from "react";
import { api } from "../../../../api";
import { OverflowFrame } from "../../../../externalComponents";
import { PageHeading } from "../../../../externalComponents/PageHeading";
import { SettingsSidebar } from "../../../../externalComponents/SettingsSidebar";
import { Button } from "../../../../externalComponents/tailwind/button";
import {
  Field,
  Fieldset,
  Legend,
} from "../../../../externalComponents/tailwind/fieldset";
import {
  Input,
  InputGroup,
} from "../../../../externalComponents/tailwind/input";
import { Text } from "../../../../externalComponents/tailwind/text";
import { CustomerContext } from "../../../../providers";
import AlcmeonIntegration from "./AlcmeonIntegration";
import ZendeskIntegration from "../../../../externalComponents/ZendeskIntegration";
import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogTitle,
} from "../../../../externalComponents/tailwind/dialog";

export default function CompanyIntegrationPage() {
  const { customer, shop } = useContext(CustomerContext);

  const urlParams = new URLSearchParams(window.location.search);
  const crispWebsiteId = urlParams.get("website_id");

  const [isLoading, setIsLoading] = useState(false);
  const [isConnectKlaviyoLoading, setIsConnectKlaviyoLoading] = useState(false);
  const [isConnectShopifyLoading, setIsConnectShopifyLoading] = useState(false);
  const [shopBillingStatus, setShopBillingStatus] = useState(null);
  const [klaviyoApiKey, setKlaviyoApiKey] = useState("");
  const [brevoApiKey, setBrevoApiKey] = useState("");
  const [brevoPhoneAttribute, setBrevoPhoneAttribute] = useState("");
  const [gorgiasApiKey, setGorgiasApiKey] = useState("");
  const [splioApiKey, setSplioApiKey] = useState("");
  const [simioApiKey, setSimioApiKey] = useState("");
  const [isSimioApiKeyVisible, setIsSimioApiKeyVisible] = useState(false);
  const [showKlaviyoDisconnectModal, setShowKlaviyoDisconnectModal] = useState(false);

  useEffect(() => {
    if (customer.klaviyoToken) setKlaviyoApiKey(customer.klaviyoToken);
    if (customer.hasApiKeyBrevo) setBrevoApiKey("*".repeat(20));
    if (customer.brevoPhoneAttribute) setBrevoPhoneAttribute(customer.brevoPhoneAttribute);
    if (customer.apiKeyGorgias) setGorgiasApiKey(customer.apiKeyGorgias);
    if (customer.apiKeySplio) setSplioApiKey(customer.apiKeySplio);
    if (customer.apiKeySimio) setSimioApiKey(customer.apiKeySimio);
  }, [customer]);

  const save = async () => {
    if (brevoApiKey && brevoApiKey !== "*".repeat(20) && !brevoPhoneAttribute) {
      alert("Please specify the Brevo attribute used for phone numbers (e.g., SMS, PHONE) when providing a Brevo API key.");
      return;
    }

    setIsLoading(true);
    const updateData = {
      klaviyoToken: klaviyoApiKey !== "" ? klaviyoApiKey : null,
      brevoPhoneAttribute: brevoPhoneAttribute !== "" ? brevoPhoneAttribute : null,
      apiKeySplio: splioApiKey !== "" ? splioApiKey : null,
    };

    if (brevoApiKey !== "*".repeat(20)) {
      updateData.apiKeyBrevo = brevoApiKey !== "" ? brevoApiKey : null;
    }

    await api.customer.update(customer.id, updateData);
    setIsLoading(false);
  };

  useEffect(() => {
    if (crispWebsiteId && !customer.crispWebsiteId) {
      api.customer.update(customer.id, {
        crispWebsiteId,
      });
      window.location.reload();
    }
  });

  const handleConnectKlaviyo = async () => {
    setIsConnectKlaviyoLoading(true);
    try {
      const installationUrl = await api.generateKlaviyoInstallationUrl({
        customerId: customer.id,
      });
      window.open(installationUrl, "_blank");
    } catch (error) {
      console.error("Error connecting to Klaviyo:", error);
    } finally {
      setIsConnectKlaviyoLoading(false);
    }
  };
  const handleDisconnectKlaviyo = () => {
    setShowKlaviyoDisconnectModal(true);
  };

  const handleConfirmKlaviyoDisconnect = async () => {
    setIsConnectKlaviyoLoading(true);
    try {
      await api.klaviyo.disconnect();
    } catch (e) {
      console.error(e);
    } finally {
      setIsConnectKlaviyoLoading(false);
      setShowKlaviyoDisconnectModal(false);
      window.location.href =
        "/settings/data/integrations/klaviyo/connexion?disconnected=true";
    }
  };

  const handleCancelKlaviyoDisconnect = () => {
    setShowKlaviyoDisconnectModal(false);
  };

  // Check billing status when shop is available
  useEffect(() => {
    const checkBillingStatus = async () => {
      if (shop?.id) {
        try {
          const response = await fetch(`/api/billing/status?shopId=${shop.id}`);
          if (response.ok) {
            const status = await response.json();
            setShopBillingStatus(status);
          }
        } catch (error) {
          console.error("Error checking billing status:", error);
        }
      }
    };

    checkBillingStatus();
  }, [shop?.id]);

  const handleConnectShopify = async () => {
    // If shop is already connected, enable subscription
    if (shop?.id) {
      setIsConnectShopifyLoading(true);
      try {
        // Call the enableSubscription action on the shopifyShop model
        const result = await api.shopifyShop.enableSubscription(shop.id);

        if (result.error) {
          console.error("Billing error:", result.error);
          alert(`Failed to activate billing: ${result.error.message}`);
          return;
        }

        // Wait 1 second to ensure data is persisted
        await new Promise((resolve) => setTimeout(resolve, 2000));

        // Fetch the updated shop record to get confirmationUrl
        const updatedShop = await api.shopifyShop.findOne(shop.id, {
          select: {
            id: true,
            confirmationUrl: true,
          },
        });

        if (!updatedShop) {
          alert("Failed to fetch shop data. Please try again.");
          return;
        }

        const confirmationUrl = updatedShop.confirmationUrl;

        if (!confirmationUrl) {
          console.error("No confirmation URL in shop:", updatedShop);
          alert("No confirmation URL received. Please try again.");
          return;
        }

        // Redirect to Shopify billing confirmation
        window.location.assign(confirmationUrl);
      } catch (error) {
        console.error("Error starting billing:", error);
        const errorMessage =
          error.message || "Failed to activate billing. Please try again.";
        alert(errorMessage);
      } finally {
        setIsConnectShopifyLoading(false);
      }
    } else {
      // No shop connected yet, go to Shopify app store
      window.open("https://apps.shopify.com/simio", "_blank");
    }
  };

  return (
    <>
      <SettingsSidebar>
        <PageHeading
          title="Integrations"
          primaryAction="Save"
          handlePrimaryAction={save}
          isPrimaryLoading={isLoading}
        />
        <OverflowFrame>
          <div className="mt-5">
            {customer && (
              <Fieldset>
                {simioApiKey && (
                  <Field>
                    <Legend>Simio API key</Legend>
                    <div className="flex flex-row">
                      <InputGroup className="flex relative items-center">
                        <Input
                          name="Simio API key"
                          value={simioApiKey}
                          disabled
                          autoComplete="off"
                          type={isSimioApiKeyVisible ? "text" : "password"}
                        />
                      </InputGroup>

                      <Button
                        color="optionButton"
                        className="ml-2"
                        onClick={() =>
                          setIsSimioApiKeyVisible((status) => !status)
                        }
                      >
                        <EyeIcon />
                      </Button>
                    </div>
                  </Field>
                )}
                <Field>
                  <Legend>Shopify</Legend>
                  {shop ? (
                    <>
                      <Text>Your account is linked to {shop.name}'s store</Text>
                      {shopBillingStatus && !shopBillingStatus.planActive && (
                        <Button
                          color="secondaryButton"
                          loading={isConnectShopifyLoading}
                          onClick={handleConnectShopify}
                          className="mt-2"
                        >
                          Activate Billing ({shopBillingStatus.planName})
                        </Button>
                      )}
                      {shopBillingStatus?.planActive && (
                        <Text className="text-green-600 mt-2">
                          ✓ Billing active: {shopBillingStatus.planName}
                        </Text>
                      )}
                    </>
                  ) : (
                    <Button
                      color="secondaryButton"
                      onClick={handleConnectShopify}
                      loading={isConnectShopifyLoading}
                    >
                      Connect Shopify
                    </Button>
                  )}
                </Field>
                <Field>
                  <Legend>Klaviyo</Legend>
                  {customer.klaviyoAccessToken && (
                    <>
                      <Text>Your account is linked to Klaviyo</Text>
                      <Button
                        color="secondaryButton"
                        loading={isConnectKlaviyoLoading}
                        onClick={handleDisconnectKlaviyo}
                      >
                        Disconnect Klaviyo
                      </Button>
                    </>
                  )}
                  {!customer.klaviyoAccessToken && customer.klaviyoToken && (
                    <Input
                      className="mb-2"
                      name="Klaviyo API key"
                      value={klaviyoApiKey}
                      onChange={(e) => setKlaviyoApiKey(e.target.value)}
                      autoComplete="off"
                      maxLength={2000}
                      type={customer.klaviyoToken ? "password" : "text"}
                    />
                  )}
                  {!customer.klaviyoAccessToken && (
                    <Button
                      color="secondaryButton"
                      loading={isConnectKlaviyoLoading}
                      onClick={handleConnectKlaviyo}
                    >
                      Connect Klaviyo
                    </Button>
                  )}
                </Field>

                <ZendeskIntegration customer={customer} />

                <Field>
                  <Legend>Brevo</Legend>
                  <Input
                    className="mb-2"
                    name="Brevo API key"
                    placeholder="Enter your Brevo API key"
                    value={brevoApiKey}
                    onChange={(e) => setBrevoApiKey(e.target.value)}
                    autoComplete="off"
                    maxLength={2000}
                    type={customer.hasApiKeyBrevo ? "password" : "text"}
                  />
                  <Input
                    name="Phone attribute name"
                    placeholder="e.g., SMS"
                    value={brevoPhoneAttribute}
                    onChange={(e) => setBrevoPhoneAttribute(e.target.value)}
                    autoComplete="off"
                    maxLength={100}
                    required={!!brevoApiKey}
                  />
                  <Text className="text-sm text-gray-600 mt-1">
                    {brevoApiKey && <span className="text-red-600">* Required - </span>}
                    Specify the Brevo contact attribute that contains phone numbers (e.g., SMS, PHONE)
                  </Text>
                </Field>

                <Field>
                  <Legend>Splio</Legend>
                  <Input
                    className="mb-2"
                    name="Splio API key"
                    value={splioApiKey}
                    onChange={(e) => setSplioApiKey(e.target.value)}
                    autoComplete="off"
                    maxLength={2000}
                    type={customer.apiKeySplio ? "password" : "text"}
                  />
                </Field>
                <Field>
                  <Legend>Gorgias API key</Legend>
                  {gorgiasApiKey ? (
                    <Input
                      name="Gorgias API key"
                      value={gorgiasApiKey}
                      disabled
                      autoComplete="off"
                      type="password"
                    />
                  ) : (
                    <Text>
                      You want to connect Simio to Gorgias? Ask our team
                    </Text>
                  )}
                </Field>
                <Field>
                  <Legend>Crisp</Legend>
                  {customer?.crispWebsiteId ? (
                    <Text>
                      Your account is linked to Crisp (Website ID:{" "}
                      {customer.crispWebsiteId})
                    </Text>
                  ) : (
                    <Button
                      color="secondaryButton"
                      onClick={() =>
                        window.open(
                          "https://app.crisp.chat/initiate/plugin/5f0419bc-3ab0-4c89-aa54-ceb55df4ce5e/",
                          "_blank",
                        )
                      }
                    >
                      Connect Crisp
                    </Button>
                  )}
                </Field>

                <AlcmeonIntegration customer={customer} />
              </Fieldset>
            )}
          </div>
        </OverflowFrame>
      </SettingsSidebar>
      <Dialog
        open={showKlaviyoDisconnectModal}
        onClose={handleCancelKlaviyoDisconnect}
      >
        <DialogTitle>Disconnect Klaviyo</DialogTitle>
        <DialogBody>
          <p>You're about to disconnect your Klaviyo account from Simio.</p>
        </DialogBody>
        <DialogActions>
          <Button
            color="secondaryButton"
            onClick={handleCancelKlaviyoDisconnect}
            disabled={isConnectKlaviyoLoading}
          >
            Cancel
          </Button>
          <Button
            color="primaryButton"
            onClick={handleConfirmKlaviyoDisconnect}
            loading={isConnectKlaviyoLoading}
          >
            Disconnect
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
